export const ADMIN_PATH = '/admin';

const PROJECT_PREFIX = `${ADMIN_PATH}/project/`;

export const projectIdFromPath = (pathname: string): string | undefined => {
  if (!pathname.startsWith(PROJECT_PREFIX)) {
    return undefined;
  }
  const id = pathname.slice(PROJECT_PREFIX.length).split('/')[0];
  return id ? decodeURIComponent(id) : undefined;
};

/** Page path with `/project/:id` removed, for matching routes and nav state. */
export const pagePath = (pathname: string): string => {
  const projectId = projectIdFromPath(pathname);
  if (!projectId) {
    return pathname;
  }
  const encoded = `${PROJECT_PREFIX}${encodeURIComponent(projectId)}`;
  const raw = `${PROJECT_PREFIX}${projectId}`;
  const prefix = pathname.startsWith(encoded)
    ? encoded
    : pathname.startsWith(raw)
    ? raw
    : undefined;
  if (!prefix) {
    return pathname;
  }
  return `${ADMIN_PATH}${pathname.slice(prefix.length)}` || ADMIN_PATH;
};

/** `/admin/project/<id>` plus the page under `/admin`. `:projectId` stays a route param. */
export const withProjectPath = (path: string, projectId?: string): string => {
  const page = pagePath(path);
  if (!projectId) {
    return page;
  }
  const id = projectId.startsWith(':')
    ? projectId
    : encodeURIComponent(projectId);
  return `${PROJECT_PREFIX}${id}${page.slice(ADMIN_PATH.length)}`;
};

export const EXTRACTION_PATH = `${ADMIN_PATH}/extraction`;

export const DOCUMENT_TYPES_PATH = `${EXTRACTION_PATH}/document-types`;

export const DOCUMENTS_PATH = `${EXTRACTION_PATH}/documents`;

export const RESULTS_PATH = `${EXTRACTION_PATH}/results`;

export const GUIDANCE_PATH = `${EXTRACTION_PATH}/guidance`;

export const CLASSIC_PHC_HOME_PATH = '/home';

export type RouteCrumb = {
  label: string;
  href?: string;
};

const extractionCrumbs = (home: string, leaf: string): RouteCrumb[] => [
  { label: 'Home', href: home },
  { label: 'AI Tools' },
  { label: 'Extraction' },
  { label: leaf },
];

export const crumbsForPath = (pathname: string): RouteCrumb[] => {
  const page = pagePath(pathname);
  const home = withProjectPath(ADMIN_PATH, projectIdFromPath(pathname));

  if (page === DOCUMENTS_PATH || page.startsWith(`${DOCUMENTS_PATH}/`)) {
    return extractionCrumbs(home, 'Documents');
  }

  if (page === RESULTS_PATH || page.startsWith(`${RESULTS_PATH}/`)) {
    return extractionCrumbs(home, 'Results');
  }

  if (page === GUIDANCE_PATH || page.startsWith(`${GUIDANCE_PATH}/`)) {
    return extractionCrumbs(home, 'Guidance');
  }

  if (page === EXTRACTION_PATH || page.startsWith(`${EXTRACTION_PATH}/`)) {
    return extractionCrumbs(home, 'Document Types');
  }

  return [{ label: 'Home' }];
};
